// src/pages/StudentRegistration.jsx
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import SummaryApi from '../common/index';

const StudentRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const encodedStudentId = queryParams.get('id');
  const isEditMode = !!encodedStudentId;
  const studentId = encodedStudentId ? atob(encodedStudentId) : null;

  const [formData, setFormData] = useState({
    full_name: '',
    category: '',
    religion: '',
    caste: '',
    dob: '',
    gender: '',
    mother_name: '',
    mobile_number: '',
    parent_mobile_number: '',
    email: '',
    aadhar: '',
    photograph: null,
    aadhar_image: null,
    father_aadhar_image: null,
    mother_aadhar_image: null,
    student_sign_image: null,
    btech_preffered_branch: '',
    address_line1: '',
    state: '',
    city: '',
    pincode: '',
    district: '',
    tenth_board: '',
    tenth_school_name: '',
    tenth_year_passing: '',
    tenth_percentage: '',
    twelfth_board_name: '',
    clg_name: '',
    twelfth_year_passing: '',
    twelfth_percentage_cgpa: '',
    twelfth_branch: '',
    ssc_memo: null,
    inter_first_year_memo: null,
    inter_second_year_memo: null,
    entrance_exam_name: '',
    declaration_agreed: false,
    is_paid: '0',
    student_enrollment_number: '',
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [aadharPreview, setAadharPreview] = useState(null);
  const [fatherAadharPreview, setFatherAadharPreview] = useState(null);
  const [motherAadharPreview, setMotherAadharPreview] = useState(null);
  const [studentSignPreview, setStudentSignPreview] = useState(null);
  const [sscMemoPreview, setSscMemoPreview] = useState(null);
  const [firstMemoPreview, setFirstMemoPreview] = useState(null);
  const [secondMemoPreview, setSecondMemoPreview] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(isEditMode);
  const [studentIdStored, setStudentIdStored] = useState(studentId);

  const photoRef = useRef();
  const aadharRef = useRef();
  const fatherAadharRef = useRef();
  const motherAadharRef = useRef();
  const studentSignRef = useRef();
  const sscMemoRef = useRef();
  const firstYearMemoRef = useRef();
  const secondYearMemoRef = useRef();

  useEffect(() => {
    if (isEditMode && studentId) {
      fetchStudentData();
    }
    return () => {
      [photoPreview, aadharPreview, fatherAadharPreview, motherAadharPreview, 
       studentSignPreview, sscMemoPreview, firstMemoPreview, secondMemoPreview]
        .forEach(preview => {
          if (preview && preview.startsWith('blob:')) {
            URL.revokeObjectURL(preview);
          }
        });
    };
  }, [isEditMode, studentId]);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      const res = await fetch(SummaryApi.getstudentByid.url, {
        method: SummaryApi.getstudentByid.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: studentId }),
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const result = await res.json();

      if (result.status === true && result.data) {
        const student = result.data;
        const formattedDob = student.dob
          ? new Date(student.dob).toISOString().split('T')[0]
          : '';

        setFormData({
          full_name: student.full_name || '',
          category: student.category || '',
          religion: student.religion || '',
          caste: student.caste || '',
          dob: formattedDob,
          gender: student.gender || '',
          mother_name: student.mother_name || '',
          mobile_number: student.mobile_number || '',
          parent_mobile_number: student.parent_mobile_number || '',
          email: student.email || '',
          aadhar: student.aadhar || '',
          photograph: null,
          aadhar_image: null,
          father_aadhar_image: null,
          mother_aadhar_image: null,
          student_sign_image: null,
          btech_preffered_branch: student.btech_preffered_branch || '',
          address_line1: student.address_line1 || '',
          state: student.state || '',
          city: student.city || '',
          pincode: student.pincode || '',
          district: student.district || '',
          tenth_board: student.tenth_board || '',
          tenth_school_name: student.tenth_school_name || '',
          tenth_year_passing: student.tenth_year_passing || '',
          tenth_percentage: student.tenth_percentage || '',
          twelfth_board_name: student.twelfth_board_name || '',
          clg_name: student.clg_name || '',
          twelfth_year_passing: student.twelfth_year_passing || '',
          twelfth_percentage_cgpa: student.twelfth_percentage_cgpa || '',
          twelfth_branch: student.twelfth_branch || '',
          ssc_memo: null,
          inter_first_year_memo: null,
          inter_second_year_memo: null,
          entrance_exam_name: student.entrance_exam_name || '',
          declaration_agreed: true,
          is_paid: student.is_paid || '0',
          student_enrollment_number: student.student_enrollment_number || '',
        });

        const baseUrl = SummaryApi.Images.url;
        if (student.photograph) setPhotoPreview(`${baseUrl}${student.photograph}`);
        if (student.aadhar_image) setAadharPreview(`${baseUrl}${student.aadhar_image}`);
        if (student.father_aadhar_image) setFatherAadharPreview(`${baseUrl}${student.father_aadhar_image}`);
        if (student.mother_aadhar_image) setMotherAadharPreview(`${baseUrl}${student.mother_aadhar_image}`);
        if (student.student_sign_image) setStudentSignPreview(`${baseUrl}${student.student_sign_image}`);
        if (student.ssc_memo && student.ssc_memo !== "N/A") setSscMemoPreview(`${baseUrl}${student.ssc_memo}`);
        if (student.inter_first_year_memo) setFirstMemoPreview(`${baseUrl}${student.inter_first_year_memo}`);
        if (student.inter_second_year_memo) setSecondMemoPreview(`${baseUrl}${student.inter_second_year_memo}`);

        setIsRegistered(true);
      } else {
        toast.error(result.message || "Failed to load student data");
      }
    } catch (err) {
      console.error("Fetch student error:", err);
      toast.error("Error loading student details.");
    } finally {
      setLoading(false);
    }
  };

  const validateName = (name) => /^[A-Za-z\s]+$/.test(name);
  const validateEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  const validateNumbersOnly = (value) => /^\d+$/.test(value);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      if (['full_name', 'mother_name', 'city', 'district', 'state', 'tenth_school_name', 'clg_name'].includes(name)) {
        if (value === '' || /^[A-Za-z\s]*$/.test(value)) {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      } else if (['tenth_year_passing', 'twelfth_year_passing'].includes(name)) {
        if (value === '' || (validateNumbersOnly(value) && value.length <= 4)) {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      } else if (['mobile_number', 'parent_mobile_number', 'aadhar'].includes(name)) {
        if (value === '' || (validateNumbersOnly(value) && value.length <= 12)) {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      } else if (['tenth_percentage', 'twelfth_percentage_cgpa'].includes(name)) {
        const percentageRegex = /^\d*\.?\d*$/;
        if (value === '' || percentageRegex.test(value)) {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }
  };

  const handleNumberOnly = (e) => {
    const char = e.key;
    if (!/[0-9]/.test(char) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(char)) {
      e.preventDefault();
    }
  };

  const handleFileChange = (e, fieldName, setPreview) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData((prev) => ({ ...prev, [fieldName]: file }));
    if (file.type.startsWith('image/')) {
      setPreview(URL.createObjectURL(file));
    } else {
      setPreview('document-icon');
    }
  };

  const getFilePreviewContent = (previewUrl, field, fileName = '') => {
    if (!previewUrl) return null;
    if (previewUrl === 'document-icon') {
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="w-20 h-28 bg-gradient-to-b from-red-50 to-red-100 rounded-lg border border-red-200 flex items-center justify-center shadow-sm relative">
            <span className="text-5xl">📄</span>
            <div className="absolute bottom-1.5 right-1.5 bg-white/80 text-[10px] px-1.5 py-0.5 rounded font-medium text-red-700">PDF</div>
          </div>
          {fileName && <span className="text-xs text-gray-600 truncate">{fileName}</span>}
          <p className="text-xs text-gray-500">Document uploaded</p>
        </div>
      );
    }

    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(previewUrl) || previewUrl.startsWith('blob:');
    if (isImage) {
      return (
        <img
          src={previewUrl}
          alt={field}
          className="max-h-48 max-w-full object-contain rounded border border-gray-200 shadow-sm"
        />
      );
    }
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="w-20 h-28 bg-gradient-to-b from-red-50 to-red-100 rounded-lg border border-red-200 flex items-center justify-center shadow-sm relative">
          <span className="text-5xl">📄</span>
        </div>
        <p className="text-xs text-gray-500">Document uploaded</p>
      </div>
    );
  };

  const validateForm = () => {
    if (!formData.full_name.trim()) return "Full Name is required";
    if (!validateName(formData.full_name)) return "Full Name should contain only letters and spaces";

    if (!formData.category) return "Category is required";
    if (!formData.btech_preffered_branch) return "Preferred Branch is required";
    if (!formData.mobile_number || formData.mobile_number.length !== 10) return "Valid Mobile Number is required";
    if (!formData.parent_mobile_number || formData.parent_mobile_number.length !== 10) return "Valid Parent Mobile Number is required";
    if (!formData.email.trim()) return "Email ID is required";
    if (!formData.aadhar || formData.aadhar.length !== 12) return "Aadhaar Number is required (12 digits)";
    if (!validateNumbersOnly(formData.aadhar)) return "Aadhaar number must contain only digits";

    if (!formData.photograph && !photoPreview) return "Student Photograph is required";
    if (!formData.aadhar_image && !aadharPreview) return "Student Aadhaar Card is required";
    if (!formData.father_aadhar_image && !fatherAadharPreview) return "Father's Aadhaar Card is required";
    if (!formData.mother_aadhar_image && !motherAadharPreview) return "Mother's Aadhaar Card is required";
    if (!formData.student_sign_image && !studentSignPreview) return "Student Signature is required";
    if (!formData.tenth_school_name.trim()) return "10th Class School Name is required";
    if (!formData.clg_name.trim()) return "12th Class College Name is required";
    if (!formData.ssc_memo && !sscMemoPreview) return "10th Class Marks Memo is required";
    if (!formData.inter_first_year_memo && !firstMemoPreview) return "12th First Year Marks Memo is required";

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errorMsg = validateForm();
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }

    setLoading(true);
    try {
      const adminId = sessionStorage.getItem('admin_id');
      if (!adminId) {
        toast.error("Admin not logged in. Please login again.");
        return;
      }

      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (['photograph', 'aadhar_image', 'father_aadhar_image', 'mother_aadhar_image',
             'student_sign_image', 'ssc_memo', 'inter_first_year_memo', 'inter_second_year_memo'].includes(key)) {
          if (value instanceof File) {
            submitData.append(key, value);
          }
        } else if (value !== null && value !== undefined && value !== '') {
          submitData.append(key, value);
        }
      });

      submitData.append('admin_id', adminId);
      submitData.append('created_date', new Date().toISOString().slice(0, 19).replace('T', ' '));
      submitData.append('is_active', '1');
      submitData.append('is_paid', formData.is_paid || '0');

      let res, result;
      if (!isRegistered) {
        res = await fetch(SummaryApi.studentRegistration.url, {
          method: SummaryApi.studentRegistration.method,
          body: submitData,
        });
        result = await res.json();

        if (result.success || result.student_id) {
          toast.success("Student registered successfully!");
          const newStudentId = result.student_id;
          setStudentIdStored(newStudentId);
          setIsRegistered(true);
          const encodedId = btoa(newStudentId.toString());
          const encodedName = btoa(formData.full_name.trim());
          navigate(`/create-studentfee?id=${encodedId}&name=${encodedName}`);
        } else {
          toast.error(result.message || "Registration failed");
        }
      } else {
        submitData.append('student_id', studentIdStored || studentId);
        res = await fetch(SummaryApi.updatestudentbyid.url, {
          method: SummaryApi.updatestudentbyid.method,
          body: submitData,
        });
        result = await res.json();

        if (result.status === true) {
          toast.success(result.message || "Student updated successfully!");
          navigate("/student-report");
        } else {
          toast.error(result.message || "Update failed");
        }
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Error during operation");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "block w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const disabledInputClass = `${inputClass} bg-gray-100 cursor-not-allowed`;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="bg-white shadow-lg rounded-xl p-6 md:p-8 border border-gray-200 relative">
        <h2 className="text-3xl font-bold text-center text-blue-900 mb-10">
          {isEditMode || isRegistered ? "Edit Student Details" : "Student Admission Form"}
        </h2>

        {loading && <div className="text-center py-4">Loading...</div>}

        <form onSubmit={handleSubmit} className="space-y-12" noValidate>
          {/* COURSE APPLYING FOR */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Course Applying For</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-xl">
              <div>
                <label className={labelClass}>B.Tech with Specialisation Name <span className="text-red-500">*</span></label>
                <select
                  name="btech_preffered_branch"
                  value={formData.btech_preffered_branch}
                  onChange={handleChange}
                  className={inputClass}
                  required
                >
                  <option value="">Select Preferred Branch</option>
                  <option value="CSE (Artificial Intelligence & Machine Learning)">CSE (Artificial Intelligence & Machine Learning)</option>
                  <option value="CSE (Artificial Intelligence & Data Science)">CSE (Artificial Intelligence & Data Science)</option>
                  <option value="CSE (Computer Science & Engineering)">CSE (Computer Science & Engineering)</option>
                  <option value="CSE (Cyber Security)">CSE (Cyber Security)</option>
                </select>
              </div>
            </div>
          </section>

          {/* PERSONAL DETAILS */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Personal Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <label className={labelClass}>Full Name <span className="text-red-500">*</span></label>
                <input
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className={isEditMode ? disabledInputClass : inputClass}
                  readOnly={isEditMode}
                  placeholder="Enter full name"
                  required
                />
              </div>

              <div>
                <label className={labelClass}>Category <span className="text-red-500">*</span></label>
                <select name="category" value={formData.category} onChange={handleChange} className={inputClass} required>
                  <option value="">Select Category</option>
                  <option value="OPEN">OPEN</option>
                  <option value="OBC">OBC</option>
                  <option value="SC">SC</option>
                  <option value="ST">ST</option>
                  <option value="VJ/DT">VJ/DT</option>
                  <option value="DT">DT</option>
                  <option value="NT-C">NT-C</option>
                  <option value="NT-B">NT-B</option>
                  <option value="NT-D">NT-D</option>
                  <option value="SBC">SBC</option>
                  <option value="EWS">EWS</option>
                  <option value="SEBC">SEBC</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Religion</label>
                <select name="religion" value={formData.religion} onChange={handleChange} className={inputClass}>
                  <option value="">Select Religion</option>
                  <option>Hindu</option>
                  <option>Muslim</option>
                  <option>Christian</option>
                  <option>Other</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Caste</label>
                <input name="caste" value={formData.caste} onChange={handleChange} className={inputClass} placeholder="e.g. BC / OC / SC / ST" />
              </div>

              <div>
                <label className={labelClass}>Date of Birth</label>
                <input type="date" name="dob" value={formData.dob} onChange={handleChange} className={inputClass} />
              </div>

              <div>
                <label className={labelClass}>Gender</label>
                <select name="gender" value={formData.gender} onChange={handleChange} className={inputClass}>
                  <option value="">Select Gender</option>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Mobile Number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  name="mobile_number"
                  maxLength={10}
                  value={formData.mobile_number}
                  onChange={handleChange}
                  onKeyPress={handleNumberOnly}
                  className={isEditMode ? disabledInputClass : inputClass}
                  readOnly={isEditMode}
                  placeholder="10 digit number"
                  required
                />
              </div>

              <div>
                <label className={labelClass}>Parents Alternative Mobile Number  <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  name="parent_mobile_number"
                  maxLength={10}
                  value={formData.parent_mobile_number}
                  onChange={handleChange}
                  onKeyPress={handleNumberOnly}
                  className={inputClass}
                  placeholder="10 digit number"
                  required
                />
              </div>

              <div>
                <label className={labelClass}>Gmail ID <span className="text-red-500">*</span></label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} placeholder="student@example.com" required />
              </div>

              <div>
                <label className={labelClass}>UIDAI Number / Aadhaar No. <span className="text-red-500">*</span></label>
                <input
                  name="aadhar"
                  maxLength={12}
                  value={formData.aadhar}
                  onChange={handleChange}
                  className={isEditMode ? disabledInputClass : inputClass}
                  readOnly={isEditMode}
                  placeholder="12 digit Aadhaar number"
                  required
                />
              </div>

              {/* File Upload Fields */}
              <div>
                <label className={labelClass}>Passport-size Photograph <span className="text-red-500">*</span></label>
                <div className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500" onClick={() => photoRef.current.click()}>
                  <p className="text-sm text-gray-500">Click to upload photo (jpg/png)</p>
                  <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileChange(e, 'photograph', setPhotoPreview)} />
                </div>
                {photoPreview && <div className="mt-4 flex flex-col items-center">{getFilePreviewContent(photoPreview, 'Photograph')}</div>}
              </div>

              <div>
                <label className={labelClass}>Aadhaar Card (Student) <span className="text-red-500">*</span></label>
                <div className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500" onClick={() => aadharRef.current.click()}>
                  <p className="text-sm text-gray-500">Click to upload Aadhaar Card (jpg/png/pdf)</p>
                  <input ref={aadharRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleFileChange(e, 'aadhar_image', setAadharPreview)} />
                </div>
                {aadharPreview && <div className="mt-4 flex flex-col items-center">{getFilePreviewContent(aadharPreview, 'Aadhaar')}</div>}
              </div>

              <div>
                <label className={labelClass}>Aadhaar Card (Father) <span className="text-red-500">*</span></label>
                <div className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500" onClick={() => fatherAadharRef.current.click()}>
                  <p className="text-sm text-gray-500">Click to upload Father's Aadhaar Card (jpg/png/pdf)</p>
                  <input ref={fatherAadharRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleFileChange(e, 'father_aadhar_image', setFatherAadharPreview)} />
                </div>
                {fatherAadharPreview && <div className="mt-4 flex flex-col items-center">{getFilePreviewContent(fatherAadharPreview, "Father's Aadhaar")}</div>}
              </div>

              <div>
                <label className={labelClass}>Aadhaar Card (Mother) <span className="text-red-500">*</span></label>
                <div className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500" onClick={() => motherAadharRef.current.click()}>
                  <p className="text-sm text-gray-500">Click to upload Mother's Aadhaar Card (jpg/png/pdf)</p>
                  <input ref={motherAadharRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleFileChange(e, 'mother_aadhar_image', setMotherAadharPreview)} />
                </div>
                {motherAadharPreview && <div className="mt-4 flex flex-col items-center">{getFilePreviewContent(motherAadharPreview, "Mother's Aadhaar")}</div>}
              </div>

              <div>
                <label className={labelClass}>Student Sign <span className="text-red-500">*</span></label>
                <div className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500" onClick={() => studentSignRef.current.click()}>
                  <p className="text-sm text-gray-500">Click to upload Signature (jpg/png)</p>
                  <input ref={studentSignRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileChange(e, 'student_sign_image', setStudentSignPreview)} />
                </div>
                {studentSignPreview && <div className="mt-4 flex flex-col items-center">{getFilePreviewContent(studentSignPreview, 'Signature')}</div>}
              </div>

              <div>
                <label className={labelClass}>Mother's Name</label>
                <input name="mother_name" value={formData.mother_name} onChange={handleChange} className={inputClass} placeholder="Enter mother's name" />
              </div>
            </div>
          </section>

          {/* ADDRESS DETAILS */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Address Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-3">
                <label className={labelClass}>Correspondence Address</label>
                <input name="address_line1" value={formData.address_line1} onChange={handleChange} placeholder="House no, Street, Area, Landmark..." className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>State</label>
                <input name="state" value={formData.state} onChange={handleChange} className={inputClass} placeholder="e.g. Andhra Pradesh" />
              </div>
              <div>
                <label className={labelClass}>City</label>
                <input name="city" value={formData.city} onChange={handleChange} className={inputClass} placeholder="e.g. Vijayawada" />
              </div>
              <div>
                <label className={labelClass}>PIN Code (6 digits)</label>
                <input name="pincode" value={formData.pincode} onChange={handleChange} className={inputClass} maxLength={6} placeholder="e.g. 520001" />
              </div>
              <div>
                <label className={labelClass}>District</label>
                <input name="district" value={formData.district} onChange={handleChange} className={inputClass} placeholder="e.g. NTR / Krishna" />
              </div>
            </div>
          </section>

          {/* Xth DETAILS */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Xth Details (SSC)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>Board</label>
                <input name="tenth_board" value={formData.tenth_board} onChange={(e) => setFormData({...formData, tenth_board: e.target.value.replace(/[^a-zA-Z\s]/g, '')})} className={inputClass} placeholder="e.g. Andhra Pradesh Board Of Secondary Education" />
              </div>
              <div>
                <label className={labelClass}>10th Class School Name <span className="text-red-500">*</span></label>
                <input name="tenth_school_name" value={formData.tenth_school_name} onChange={handleChange} className={inputClass} placeholder="Enter school name" required />
              </div>
              <div>
                <label className={labelClass}>Year of Passing</label>
                <input name="tenth_year_passing" value={formData.tenth_year_passing} onChange={handleChange} className={inputClass} maxLength={4} placeholder="e.g. 2024" />
              </div>
              <div>
                <label className={labelClass}>Percentage</label>
                <input name="tenth_percentage" value={formData.tenth_percentage} onChange={handleChange} className={inputClass} placeholder="e.g. 85.5" />
              </div>
              <div>
                <label className={labelClass}>Mark sheet of Class 10 (SSC) Marks <span className="text-red-500">*</span></label>
                <div className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500" onClick={() => sscMemoRef.current.click()}>
                  <p className="text-sm text-gray-500">Upload SSC Memo (PDF / Image)</p>
                  <input ref={sscMemoRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => handleFileChange(e, 'ssc_memo', setSscMemoPreview)} />
                </div>
                {sscMemoPreview && <div className="mt-5 flex flex-col items-center">{getFilePreviewContent(sscMemoPreview, 'SSC Memo')}</div>}
              </div>
            </div>
          </section>

          {/* XIIth DETAILS */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">XIIth Details (Intermediate)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>Board</label>
                <select name="twelfth_board_name" value={formData.twelfth_board_name} onChange={handleChange} className={inputClass}>
                  <option value="">Select Board</option>
                  <option>Board of Intermediate Education, Andhra Pradesh</option>
                  <option>Board of Intermediate Education, Telangana</option>
                  <option>CBSE</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>12th Class College Name <span className="text-red-500">*</span></label>
                <input name="clg_name" value={formData.clg_name} onChange={handleChange} className={inputClass} placeholder="Enter college name" required />
              </div>
              <div>
                <label className={labelClass}>Year of Passing</label>
                <select name="twelfth_year_passing" value={formData.twelfth_year_passing} onChange={handleChange} className={inputClass}>
                  <option value="">Select Year</option>
                  <option value="2026">2026</option>
                  <option value="2025">2025</option>
                  <option value="2024">2024</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Percentage / CGPA</label>
                <input name="twelfth_percentage_cgpa" value={formData.twelfth_percentage_cgpa} onChange={handleChange} className={inputClass} placeholder="e.g. 9.24 or 85.5" />
              </div>
              <div>
                <label className={labelClass}>Branch</label>
                <select name="twelfth_branch" value={formData.twelfth_branch} onChange={handleChange} className={inputClass}>
                  <option value="">Select Branch</option>
                  <option>Science</option>
                  <option>Commerce</option>
                  <option>Arts</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Mark sheet of Class 12 (HSC) First 1st Year Marks <span className="text-red-500">*</span></label>
                <div className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500" onClick={() => firstYearMemoRef.current.click()}>
                  <p className="text-sm text-gray-500">Upload 1st Year Memo (PDF / Image)</p>
                  <input ref={firstYearMemoRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => handleFileChange(e, 'inter_first_year_memo', setFirstMemoPreview)} />
                </div>
                {firstMemoPreview && <div className="mt-5 flex flex-col items-center">{getFilePreviewContent(firstMemoPreview, '1st Year Memo')}</div>}
              </div>

              <div>
                <label className={labelClass}>Inter 2nd Year Marks Memo</label>
                <div className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500" onClick={() => secondYearMemoRef.current.click()}>
                  <p className="text-sm text-gray-500">Upload 2nd Year Memo (PDF / Image)</p>
                  <input ref={secondYearMemoRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => handleFileChange(e, 'inter_second_year_memo', setSecondMemoPreview)} />
                </div>
                {secondMemoPreview && <div className="mt-5 flex flex-col items-center">{getFilePreviewContent(secondMemoPreview, '2nd Year Memo')}</div>}
              </div>
            </div>
          </section>

          {/* ENTRANCE EXAM DETAILS */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Entrance Exam Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-xl">
              <div>
                <label className={labelClass}>Entrance Exam Name</label>
                <input name="entrance_exam_name" value={formData.entrance_exam_name} onChange={handleChange} placeholder="e.g. AP EAMCET, JEE Main, NEET, Others" className={inputClass} />
              </div>
            </div>
          </section>

          {/* DECLARATION */}
          <section className="border-t pt-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="declaration_agreed"
                checked={formData.declaration_agreed}
                onChange={handleChange}
                className="mt-1 w-5 h-5 accent-blue-600"
              />
              <span className="text-sm text-gray-700 leading-relaxed">
                I hereby declare that the information furnished by me in the admission application submitted to Sanjay Ghodawat University Kolhapur is true and correct to the best of my knowledge and belief.
              </span>
            </label>
          </section>

          {/* SUBMIT BUTTON */}
          <div className="text-end mt-10">
            <button
              type="submit"
              className={`px-12 py-3 rounded-lg bg-blue-700 hover:bg-blue-800 text-white font-semibold text-lg transition shadow-md ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
              disabled={loading}
            >
              {loading ? 'Processing...' : isEditMode || isRegistered ? 'UPDATE STUDENT' : 'NEXT'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentRegistration;